if (!customElements.get('pdp-aov-boaster')) {
  customElements.define('pdp-aov-boaster', class PdpAovBoaster extends HTMLElement {
    connectedCallback() {
      if (this.dataset.aovObserved === 'true') return;
      this.dataset.aovObserved = 'true';

      this.isActive = false;
      this.observer = null;

      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        this.activate();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.activate();
        this.observer?.disconnect();
        this.observer = null;
      }, {
        rootMargin: '350px 0px',
        threshold: 0.01,
      });

      this.observer.observe(this);
    }

    activate() {
      if (this.isActive) return;
      this.isActive = true;
      this.dataset.aovReady = 'true';

      this.items = Array.from(this.querySelectorAll('[data-addon]'));
      this.summary = this.querySelector('[data-summary-copy]');
      this.threshold = this.querySelector('[data-threshold-copy]');
      this.progress = this.querySelector('[data-progress]');
      this.error = this.querySelector('[data-aov-error]');
      this.copy = JSON.parse(this.querySelector('[data-aov-copy]')?.textContent || '{}');
      this.basePrice = Number(this.dataset.basePrice || 0);
      this.cartTotal = Number(this.dataset.cartTotal || 0);
      this.deliveryThreshold = Number(this.dataset.deliveryThreshold || 0);
      this.giftThreshold = Number(this.dataset.giftThreshold || 0);
      this.currency = this.dataset.currency || 'GBP';
      this.selected = new Map();

      this.onItemClick = this.onItemClick.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.refreshCartTotal = this.refreshCartTotal.bind(this);
      this.onCartChanged = this.onCartChanged.bind(this);
      this.onExternalCartUpdated = this.onExternalCartUpdated.bind(this);

      this.items.forEach((item) => item.addEventListener('click', this.onItemClick));
      document.addEventListener('change', this.onDocumentChange);
      window.addEventListener('cardology:pdp-cart-added', this.refreshCartTotal);
      window.addEventListener('cardology:cart-changed', this.onCartChanged);
      window.addEventListener('oe:cart:updated', this.onExternalCartUpdated);
      document.addEventListener('cart:updated', this.refreshCartTotal);
      document.addEventListener('cart:refresh', this.refreshCartTotal);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.refreshCartTotal
        );
      }

      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;

      if (!this.isActive) return;
      this.items?.forEach((item) => item.removeEventListener('click', this.onItemClick));
      document.removeEventListener('change', this.onDocumentChange);
      window.removeEventListener('cardology:pdp-cart-added', this.refreshCartTotal);
      window.removeEventListener('cardology:cart-changed', this.onCartChanged);
      window.removeEventListener('oe:cart:updated', this.onExternalCartUpdated);
      document.removeEventListener('cart:updated', this.refreshCartTotal);
      document.removeEventListener('cart:refresh', this.refreshCartTotal);

      if (typeof this.cartUpdateUnsubscriber === 'function') {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
    }

    onExternalCartUpdated(event) {
      const cart = event?.detail?.cart;

      if (cart) {
        this.cartTotal = Math.max(
          Number(cart.total_price ?? cart.items_subtotal_price ?? 0),
          0
        );
        this.render();
        return;
      }

      this.refreshCartTotal();
    }

    onCartChanged(event) {
      const totalPrice = Number(event?.detail?.totalPrice);

      if (Number.isFinite(totalPrice)) {
        this.cartTotal = Math.max(totalPrice, 0);
        this.render();
        return;
      }

      this.refreshCartTotal();
    }

    async refreshCartTotal() {
      try {
        const response = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) return;

        const cart = await response.json();
        this.cartTotal = Number(cart.total_price || 0);
        this.render();
      } catch (error) {
        console.warn('[Cardology PDP AOV] Unable to refresh cart total.', error);
      }
    }

    money(cents) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(cents / 100);
    }

    onItemClick(event) {
      const item = event.currentTarget;
      if (item.disabled) return;

      const id = Number(item.dataset.addonId);
      if (!id) return;

      if (this.selected.has(id)) {
        this.selected.delete(id);
      } else {
        this.selected.set(id, {
          id,
          quantity: 1,
          price: Number(item.dataset.addonPrice || 0),
          name: item.dataset.addonName || '',
        });
      }

      const isSelected = this.selected.has(id);
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      this.render();
    }

    onDocumentChange(event) {
      const variantElement = event.target.closest('variant-radios, variant-selects');
      if (!variantElement) return;

      requestAnimationFrame(() => {
        if (variantElement.currentVariant?.price != null) {
          this.basePrice = Number(variantElement.currentVariant.price);
          this.render();
        }
      });
    }

    getTotals() {
      const selectedAddons = Array.from(this.selected.values());
      const addonsTotal = selectedAddons.reduce((total, addon) => total + addon.price, 0);
      return {
        selectedAddons,
        selectedCount: selectedAddons.length,
        addonsTotal,
        basketTotal: this.basePrice + addonsTotal,
      };
    }

    getSelectedAddons() {
      if (!this.selected) return [];
      return Array.from(this.selected.values()).map((addon) => ({ ...addon }));
    }

    clearSelection() {
      if (!this.selected) return;

      this.selected.clear();
      this.items?.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
        item.setAttribute('aria-checked', 'false');
      });

      this.showError();
      this.render();
    }

    render() {
      const totals = this.getTotals();
      const deliveryThreshold = Math.max(this.deliveryThreshold, 1);
      const giftThreshold = Math.max(this.giftThreshold, deliveryThreshold);
      const actualCartTotal = Math.max(this.cartTotal, 0);
      const motivatorTotal = actualCartTotal + totals.addonsTotal;
      const nextThreshold = motivatorTotal < deliveryThreshold ? deliveryThreshold : giftThreshold;
      const percentage = Math.min((motivatorTotal / nextThreshold) * 100, 100);

      if (this.summary) {
        this.summary.innerHTML = totals.selectedCount === 0
          ? this.copy.empty
          : `<strong>${totals.selectedCount} ${this.copy.selected}</strong> · ${this.copy.basketLabel} <strong>${this.money(totals.basketTotal)}</strong>`;
      }

      if (this.threshold) {
        if (motivatorTotal >= giftThreshold) {
          this.threshold.textContent = this.copy.allUnlocked;
        } else if (motivatorTotal >= deliveryThreshold) {
          this.threshold.textContent = `${this.copy.deliveryUnlocked} · ${this.money(giftThreshold - motivatorTotal)} ${this.copy.giftAway}`;
        } else {
          this.threshold.textContent = `${this.money(deliveryThreshold - motivatorTotal)} ${this.copy.deliveryAway}`;
        }
      }

      if (this.progress) this.progress.style.width = `${percentage}%`;

      window.dispatchEvent(new CustomEvent('cardology:aov-addons-change', {
        detail: {
          selectedAddons: totals.selectedAddons,
          selectedIds: totals.selectedAddons.map((addon) => addon.id),
          addonsTotal: totals.addonsTotal,
          basketTotal: totals.basketTotal,
        },
      }));
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  });
}
